import { useEffect, useRef } from "react";

function useHeroParallax({ speedVideo = 0.3, speedText = 0.15 } = {}) {
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const textGroup = hero.querySelector("#hero-text-group");

    let heroTop = 0, heroHeight = 0, ticking = false;

    const measure = () => {
      const r = hero.getBoundingClientRect();
      heroTop = r.top + window.scrollY;
      heroHeight = r.height || window.innerHeight;
    };

    const update = () => {
      ticking = false;
      const scrollY = window.scrollY;
      const progress = (scrollY - heroTop) / heroHeight;

      // background video (slow, same direction)
      hero.style.setProperty(
        "--parallax-offset",
        `${progress * heroHeight * speedVideo}px`
      );

      // text plane (opposite, subtle)
      textGroup?.style.setProperty(
        "--text-parallax-offset",
        `${-progress * heroHeight * speedText}px`
      );
    };

    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    const onResize = () => { measure(); update(); };

    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const applyMotionPref = () => {
      if (mql.matches) {
        hero.style.removeProperty("--parallax-offset");
        textGroup?.style.removeProperty("--text-parallax-offset");
        window.removeEventListener("scroll", onScroll);
      } else {
        window.addEventListener("scroll", onScroll, { passive: true });
        update();
      }
    };

    const ro = new ResizeObserver(() => { measure(); update(); });
    ro.observe(hero);

    measure();
    applyMotionPref();
    window.addEventListener("resize", onResize);
    mql.addEventListener?.("change", applyMotionPref);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      mql.removeEventListener?.("change", applyMotionPref);
      ro.disconnect();
    };
  }, [speedVideo, speedText]);

  return heroRef;
}

export default useHeroParallax;
